/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-spread */
import {
  Text
} from "@mantine/core";
import { kaReducer, ITableProps } from "ka-table";
import {
  loadData,
  updateData,
  showLoading,
  hideLoading,
  updatePageIndex,
  updatePagesCount
} from "ka-table/actionCreators";
import { ActionType, DataType, SortDirection, SortingMode } from "ka-table/enums";
import { DispatchFunc } from "ka-table/types";
import { useEffect, useState } from "react";
import useWorkspaceStore from "../../store/workspace/workspaceStore";
import { getWorkspaceTrendsAgaintQuery } from "../../services/workspace/workspaceEndpoints";
import { useTraitsAndMetrics } from "../../hooks/QueryHooks";
import parseTableData from "../../utils/parseTable";
import KaTable, { ISortBy } from "../table/KaTable";

import EmptySignalBodyPlaceholder from "./EmptySignalBodyPlaceholder";

type Props = {
  tab: "accounts" | "users",
  workspaceId: string,
  tableHeight?: number,
  containerClass?: string,
  automationsList: any[],
  onSignalClick?: (id: string) => void,
  owner?: any[],
  signalStatus?: string,
  automationId?: string,
};

const SignalsBody = ({
  tab, workspaceId, tableHeight, containerClass, automationsList = [], onSignalClick = () => { }, owner = [], signalStatus, automationId,
}: Props) => {
  // Find the selected automation based on automationId prop
  const selectedAutomation = automationsList.find(automation => automation.id === automationId) || automationsList[0];
  const [total, setTotal] = useState<number>(0);
  const workspace = useWorkspaceStore((state) => state.workspace);

  // Build filter object for status and owner filtering
  const buildFilterObject = (automation: any) => {
    const predicates = [];

    // Add status filter if selected
    if (signalStatus) {
      predicates.push({
        field: "sr.status",
        operator: "eq",
        value: signalStatus,
        type: "string",
        entity_type: "column"
      });
    }

    // Add owner filter if selected
    if (owner && owner.length > 0 && !owner.includes("all")) {
      // Check if only "unassigned" is selected
      if (owner.length === 1 && owner[0] === "unassigned") {
        // Use 'unknown' operator for unassigned owners
        predicates.push({
          field: "sr.owner_id",
          operator: "unknown",
          type: "string",
          entity_type: "column",
          value: ""
        });
      } else if (owner.includes("unassigned")) {
        // If "unassigned" is one of multiple selections, we need OR within owner filter
        // One for unknown and one for the specific owners
        const nonUnassignedOwners = owner.filter(o => o !== "unassigned");
        predicates.push({
          query_type: "OR",
          predicates: [
            {
              field: "sr.owner_id",
              operator: "unknown",
              type: "string",
              entity_type: "column",
              value: '',
            },
            {
              field: "sr.owner_id",
              operator: "in",
              value: nonUnassignedOwners,
              type: "string",
              entity_type: "column"
            }
          ]
        });
      } else {
        // Regular case - specific owners selected
        predicates.push({
          field: "sr.owner_id",
          operator: "in",
          value: owner,
          type: "string",
          entity_type: "column"
        });
      }
    }

    // Return empty filter if no predicates
    if (predicates.length === 0) {
      return {};
    }

    // Return filter object with AND logic at the top level
    return {
      query_type: "AND",
      predicates
    };
  };

  // Get traits and metrics for the current tab
  const traitsAndMetrics: any = useTraitsAndMetrics({
    workspaceId: workspaceId || "",
    enabled: !!workspaceId,
  });

  const traits: any = tab === "accounts" ? traitsAndMetrics?.data?.accountTraits : traitsAndMetrics?.data?.contactTraits;
  const metrics: any = tab === "accounts" ? traitsAndMetrics?.data?.accountMetrics : traitsAndMetrics?.data?.contactMetrics;

  // KaTable state and dispatcher
  const [tableProps, changeTableProps] = useState<ITableProps>({
    columns: [],
    columnResizing: true,
    paging: {
      enabled: true,
      pageIndex: 0,
      pagesCount: 1,
      pageSize: 15,
    },
    columnReordering: true,
    rowKeyField: "randomId",
    height: (tableHeight || 0) > 300 ? tableHeight : 300,
    singleAction: loadData(),
    sortingMode: SortingMode.SingleRemote,
    loading: {
      enabled: true,
      text: "Loading",
    },
  });

  const kaDispatch: DispatchFunc = (action: any) => {
    changeTableProps((prevState: ITableProps) => kaReducer(prevState, action));
  };

  // Fetch data using automation filters
  const fetchData = async (page: number, sortedBy: ISortBy, automation: any) => {
    if (workspaceId && automation && traits?.obj && metrics?.obj) {
      kaDispatch(showLoading());
      try {
        const response: any = await getWorkspaceTrendsAgaintQuery({
          workspaceId,
          entityType: automation.entity_relation,
          query: {
            include_count: true,
            page: page + 1,
            per_page: 15,
            filter: buildFilterObject(automation),
            automation_id: automation.id,
            selected_fields: automation?.selected_fields ? [...automation.selected_fields, "sr.status", "sr.id", "sr.automation_id", "sr.owner_id", ...(automation.entity_relation === "accounts" ? ["accounts.name", "accounts.external_id"] : ["contacts.name", "contacts.external_id"])] : [...(automation.entity_relation === "accounts" ? ["accounts.name", "accounts.external_id"] : ["contacts.name", "contacts.external_id"]), 'sr.status', "sr.id", "sr.automation_id", "sr.owner_id"],
            sort: {
              field: sortedBy?.key || automation?.sort?.field || (automation.entity_relation === "accounts" ? "accounts.last_seen" : "users.last_seen"),
              order: sortedBy?.direction ? (sortedBy?.direction === SortDirection.Ascend ? "asc" : "desc") : automation?.sort?.order ? (automation?.sort?.order === SortDirection.Ascend ? "asc" : "desc") : "desc",
              type: sortedBy?.type || automation?.sort?.type || "property",
            },
            metrics: automation.metrics || [],
            time_window: automation.time_window,
            time_zone: automation.time_zone,
            search_signal_runs: true,
          },
        });

        if (response?.data) {
          setTotal(response.total_results);
          const list = parseTableData({ data: response.data, memberData: [] });
          console.log(list);
          kaDispatch(updateData(list));
          kaDispatch(updatePagesCount(Math.ceil(response.total_results / (tableProps?.paging?.pageSize ?? 1)) || 0));
          kaDispatch(hideLoading());
        } else {
          setTotal(0);
          kaDispatch(updatePageIndex(0));
          kaDispatch(updateData([]));
          kaDispatch(updatePagesCount(1));
          kaDispatch(hideLoading());
        }
      } catch (err: any) {
        setTotal(0);
        kaDispatch(updatePageIndex(0));
        kaDispatch(updateData([]));
        kaDispatch(updatePagesCount(1));
        kaDispatch(hideLoading());
      }
    }
  };

  // Setup columns based on selected automation
  useEffect(() => {
    if (selectedAutomation && traits?.obj && metrics?.obj) {
      // build unique column list
      const baseCols = new Set<string>([
        ...(selectedAutomation?.selected_fields || []),
        ...(selectedAutomation?.metrics || []),
      ]);

      // always-required columns
      ["sr.status"].forEach(col => baseCols.add(col));

      // entity-specific defaults
      if (selectedAutomation.entity_relation === "accounts") {
        ["accounts.name", "accounts.external_id"].forEach(col => baseCols.add(col));
      } else if (selectedAutomation.entity_relation === "contacts") {
        ["contacts.name", "contacts.external_id"].forEach(col => baseCols.add(col));
      }

      const columns = Array.from(baseCols)
        .reduce((arr: any, item: any) => {
          if (item === "sr.status") {
            arr.push({
              key: "sr.status",
              title: "Status",
              dataType: DataType.String,
              sortDirection: SortDirection.Ascend,
              width: 120,
            });
          }
          if (item === "sr.owner_id") {
            arr.push({
              key: "sr.owner_id",
              title: "Owner",
              dataType: DataType.String,
              sortDirection: SortDirection.Ascend,
              width: 150,
            });
          }
          if (!{ ...traits.obj, ...metrics?.obj }[item] || { ...traits?.obj, ...metrics?.obj }[item]?.exclude_display) {
            return arr;
          }

          const column: any = {
            key: { ...traits?.obj, ...metrics?.obj }[item].name,
            dataType: !item.includes("metric") ? { ...traits?.obj, ...metrics?.obj }[item].data_type : "object",
            title: { ...traits?.obj, ...metrics?.obj }[item].display_name,
            isResizable: true,
            colGroup: { style: { minWidth: 50, width: 200 } },
            isSortable: true,
            attributeType: { ...traits?.obj, ...metrics?.obj }[item].attribute_type,
          };

          if (item === "accounts.name" || item === "users.name") {
            arr.unshift(column);
          } else {
            arr.push(column);
          }
          return arr;
        }, []);

      changeTableProps((state: any) => ({
        ...state,
        columns,
      }));
    }
  }, [selectedAutomation, traits?.obj, metrics?.obj]);

  // No need for the useEffect that sets the first automation as default since we handle it in the component declaration

  // Fetch data when automation, automationId, owner, signalStatus, or pagination changes
  useEffect(() => {
    if (selectedAutomation) {
      fetchData(tableProps?.paging?.pageIndex || 0, { key: "", direction: SortDirection.Ascend }, selectedAutomation);
    }
  }, [automationId, selectedAutomation, tableProps?.paging?.pageIndex, owner, signalStatus]);

  const handleRowClick = (row: any) => {
    const accountId = row[tab === "accounts" ? "accounts.name" : "users.name"]?.externalId || row.randomId;
    // Pass only the accountId as per the type definition
    onSignalClick(accountId);
  };

  return (
    <div className={`bg-white rounded-b-lg overflow-hidden ${containerClass || ""}`}>
      {/* Automation selector removed - now using the one from SignalHeader */}

      {/* Results count */}
      <div className="px-4 py-2 border-b border-gray-100">
        <Text size="xs" fw={600} c="text.5" className="italic">
          {total} Results {selectedAutomation && `for ${selectedAutomation.name}`}
        </Text>
      </div>

      {/* Table view */}
      <div className="overflow-x-auto">
        {!selectedAutomation ? (
          <EmptySignalBodyPlaceholder height="full" />
        ) : (
          <KaTable
            tableProps={tableProps}
            changeTableProps={changeTableProps}
            kaDispatch={kaDispatch}
            rowKeyField="randomId"
            itemType={tab === "accounts" ? "Accounts" : "Contacts"}
            totalData={total}
            onRowClick={handleRowClick}
            noDataTitle={tab === "accounts" ? "No Accounts Available" : "No Contacts Available"}
            noDataMessage="We couldn't find any data for the given filters"
          />
        )}
      </div>
    </div>
  );
};

export default SignalsBody;
